import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert, CmdError } from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import localforage from 'localforage';
import { useQueries } from '@tanstack/react-query';
import { IWidgetGraph } from '../../OnlinePanel/OnlinePanelState';
import { IVehicle, IVehicleData } from '../../Vehicles/types';
import { URL_GRAPHS } from '../../../router/constants';
import {
  getData,
  getDataByRideId,
  getVehicleName,
  getIsCompressed,
  getDataByDates,
  getNameFromUrl,
  getIsEndDateToday,
  getIsFilterableByRideId,
  IGraphResponseData,
  getPartData,
  getPartDataByRideId,
  getDateFrom,
  getDateTo,
  getPartDataByDates,
  getRideStartDateAndAddress,
  getRideEndDateAndAddress,
  todayDateRange,
} from '../GraphService';
import { FUEL_LEVEL_LITERS } from '../../OnlinePanel/Widgets/FuelLevelLitersWidget';
import OnlineGraph from './OnlineGraph';
import TableDatepicker from '../TableDatepicker';
import Stopped from './Stopped';
import { RPM } from '../../OnlinePanel/Widgets/RpmWidget';
import SelectRidesModal from '../SelectRidesModal';
import RideStartAndEnd from '../RideStartAndEnd';
import PartSelecting from '../PartSelecting';
import { BATTERY_VOLTAGE } from '../../OnlinePanel/Widgets/BatteryWidget';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import PositionDetails from '../PositionsDetails';
import {
  isGraphOpenAtom,
  lastClickedTimestampAtom,
  ordinaryWidgetGraphAtom,
  widgetGraphSelectedValuesAtom,
} from '../GraphState';
import { setLoaderAtom } from '../../../store/recoil/AppState';
import { vehicleDataByIdSelector } from '../../../store/recoil/vehiclesData';
import { vehicleByIdSelector } from '../../../store/recoil/vehicles';
import useAnalytics from '../../../hooks/useAnalytics';

interface IOrdinaryGraphProps {
  vehicleId?: number;
  name?: string;
}

export default function OrdinaryGraphOnline(props: IOrdinaryGraphProps) {
  const { trackEvent } = useAnalytics();
  const [location] = useLocation();
  const [, params] = useRoute(`${URL_GRAPHS}/:name/:vehicleId`);
  const { formatMessage: f } = useIntl();

  const setLastClickedTimestamp = useSetRecoilState<string>(lastClickedTimestampAtom);
  const [isFilterableByRideId, setIsFilterableByRideId] = React.useState(
    props.name ? getIsFilterableByRideId(props.name) : false
  );
  const setWidgetGraphSelectedValues = useSetRecoilState<string[] | null>(
    widgetGraphSelectedValuesAtom
  );
  const [selectedPartDate, setSelectedPartDate] = React.useState<Date[] | null>(null);
  const [origStatus, setOrigStatus] = React.useState('');
  const [rangeError, setRangeError] = React.useState('');
  const [name, setName] = React.useState(props.name || '');
  const [isToday, setIsToday] = React.useState(true);
  const [defaultDates, setDefaultDates] = React.useState<Date[] | undefined>(undefined);
  const vehicleData = useRecoilValue<IVehicleData | null>(
    vehicleDataByIdSelector(props.vehicleId || Number(params?.vehicleId) || 0)
  );
  const [startDateAndAddress, setStartDateAndAddress] = React.useState('');
  const [lastDateAndAddress, setLastDateAndAddress] = React.useState('');
  const [endRideDateAndAddress, setEndDateAndAddress] = React.useState('');
  const [showSelectRidesModal, setShowSelectRidesModal] = React.useState(false);
  const [rideId, setRideId] = React.useState('');
  const [responseData, setResponseData] = React.useState<IGraphResponseData[]>([]);
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [isOrigCompressed, setIsOrigCompressed] = React.useState(false);
  const [ordinaryWidgetGraph, setOrdinaryWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    ordinaryWidgetGraphAtom
  );
  const [selectedDate, setSelectedDate] = React.useState<Date[]>(todayDateRange);
  const vehicle = useRecoilValue<IVehicle | null>(
    vehicleByIdSelector(
      props.vehicleId || (params && params.vehicleId ? Number(params.vehicleId) : false)
    )
  );
  const setIsGraphOpen = useSetRecoilState<boolean | number>(isGraphOpenAtom);
  const { setDocumentTitle } = useDocumentTitle();

  const getTitle = React.useCallback(() => {
    if (name === FUEL_LEVEL_LITERS) {
      return f({ id: 'widget.fuelLevel.tooltip' }, { valueType: 'L' });
    }
    if (name === RPM) {
      return f({ id: 'infoWindow.data.engineRpm' });
    }
    if (name === BATTERY_VOLTAGE) {
      return f({ id: 'infoWindow.data.voltage' });
    }
    return f({
      id: `graph.${name}.page.title`,
    });
  }, [f, name]);

  const resetWidgetGraphSelectedValues = () => {
    setWidgetGraphSelectedValues(null);
    setSelectedPartDate(null);
    setSelectedDate(todayDateRange);
  };

  const resetGraphStates = () => {
    setResponseData([]);
    setLastClickedTimestamp('');
    setStartDateAndAddress('');
    setLastDateAndAddress('');
    setEndDateAndAddress('');
    setIsOrigCompressed(false);
    setOrigStatus(rideId ? 'STOP' : '');
  };

  React.useMemo(() => {
    if (params && params.name) {
      const title = getNameFromUrl(`/${params.name}`);
      setName(title);
      setIsFilterableByRideId(getIsFilterableByRideId(title));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  React.useEffect(() => {
    if (vehicle) {
      setIsGraphOpen(vehicle.id);
    }
  }, [setIsGraphOpen, vehicle]);

  React.useEffect(() => {
    if (vehicle && params?.vehicleId) {
      const documentTitle = `${getTitle()} ${getVehicleName(vehicle)}`;
      setDocumentTitle(documentTitle);
    }
  }, [getTitle, params, setDocumentTitle, vehicle]);

  const handleData = async () => {
    if (vehicle) {
      resetWidgetGraphSelectedValues();
      setLoader(true);
      const response = isFilterableByRideId
        ? await getData(name, String(vehicle.id))
        : await getDataByDates(name, String(vehicle.id));
      if (response) {
        if (isFilterableByRideId) {
          if (vehicleData?.status === 'STOP') {
            localforage.setItem('widgetGraph', {
              vehicleId: vehicle.id,
              data: response,
              name,
              rideId: null,
            });
          } else {
            localforage.setItem('widgetGraph', null);
          }
        }
        if (ordinaryWidgetGraph) {
          setOrdinaryWidgetGraph((oldState: any) => {
            return { ...oldState, timestamp: response[0]?.timestamp, name };
          });
        }
        setResponseData(response);
        if (params) {
          setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        }
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
        setIsOrigCompressed(getIsCompressed(response));
      } else if (!params && ordinaryWidgetGraph) {
        setOrdinaryWidgetGraph((oldState: any) => {
          return { ...oldState, timestamp: '', name };
        });
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const handleDataByRideId = async (id: string): Promise<void> => {
    if (vehicle) {
      setLoader(true);
      const response = await getDataByRideId(name, String(vehicle.id), id);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        setRideId(id);
        setIsOrigCompressed(getIsCompressed(response));
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
        localforage.setItem('widgetGraph', {
          vehicleId: vehicle.id,
          data: response,
          name,
          rideId: id,
        });
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const handleDataByDates = async (dates?: Date[] | null): Promise<void> => {
    if (vehicle) {
      resetWidgetGraphSelectedValues();
      if (dates) {
        setIsToday(getIsEndDateToday(dates[1]));
        setSelectedDate(dates);
        setSelectedPartDate(null);
        setRangeError('');
        setLoader(true);
        const response = await getDataByDates(name, String(vehicle.id), dates);
        if (response) {
          setResponseData(response);
          setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        } else {
          setResponseData([]);
          setLastClickedTimestamp('');
        }
        setLoader(false);
      } else if (dates === null) {
        setRangeError(f({ id: 'datepicker.spanFromToValidation' }));
      } else {
        setRangeError(f({ id: 'errors.reports.cannotSelectDateLongerThanMonth' }));
      }
    }
  };

  const reloadData = async (): Promise<void> => {
    if (vehicle) {
      resetWidgetGraphSelectedValues();
      if (selectedDate && selectedDate.length === 2) {
        setIsToday(getIsEndDateToday(selectedDate[1]));
        setRangeError('');
        setLoader(true);
        const response = await getDataByDates(name, String(vehicle.id), selectedDate);
        if (response) {
          setResponseData(response);
          if (params) {
            setLastClickedTimestamp(response[response.length - 1]?.timestamp);
          }
        } else {
          setResponseData([]);
          setLastClickedTimestamp('');
        }
        setLoader(false);
      }
    }
  };

  const getDataFromLocalforage = async (): Promise<void> => {
    if (isFilterableByRideId && vehicle) {
      const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
      if (
        data &&
        data.data &&
        data.vehicleId === vehicle.id &&
        data.name === name &&
        !data.rideId
      ) {
        setResponseData(data.data);
        if (params) {
          setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
        }
        setIsOrigCompressed(getIsCompressed(data.data));
        setStartDateAndAddress(getRideStartDateAndAddress(data.data));
        setLastDateAndAddress(getRideEndDateAndAddress(data.data));
      } else {
        handleData();
      }
    } else {
      handleData();
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['ordinaryGraphData', vehicle?.id],
        queryFn: () => getDataFromLocalforage(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!(params || (!params && ordinaryWidgetGraph && !ordinaryWidgetGraph.timestamp)),
      },
    ],
  });

  React.useEffect(() => {
    if (vehicleData?.status && !origStatus) {
      setOrigStatus(vehicleData.status);
    }
  }, [origStatus, vehicleData?.status]);

  const handleModal = () => {
    trackEvent([
      'Ordinary graph page',
      'click',
      showSelectRidesModal ? 'Close select ride modal' : 'Open select ride modal',
    ]);
    setShowSelectRidesModal(!showSelectRidesModal);
  };

  const handleRide = (id: string, dates: Date[]) => {
    trackEvent(['Ordinary graph page', 'click', 'Select ride']);
    setWidgetGraphSelectedValues(null);
    handleModal();
    handleDataByRideId(id);
    setDefaultDates(dates);
  };

  const handleEndRideDateAndAddress = (address: string) => {
    setEndDateAndAddress(address);
  };

  const getIsOffline = () => {
    if (name === RPM) {
      return origStatus === 'STOP' || rideId;
    }
    return !isToday;
  };

  // const getEndOfToday = () => {
  //   if (responseData && responseData[0]) {
  //     const ts = new Date(responseData[0].timestamp);
  //     return new Date(ts.setHours(23, 59, 59, 999));
  //   }
  //   return new Date(new Date().setHours(23, 59, 59, 999));
  // };

  const handleDataFromNewRide = () => {
    setOrigStatus('DRIVING');
    setRideId('');
    handleData();
  };

  const handleDataAfterFinishedRide = () => {
    setOrigStatus('STOP');
    handleData();
  };

  const renderGraph = () => {
    if (!origStatus) {
      return <div />;
    }
    const isCompressed = getIsCompressed(responseData);
    if (vehicle) {
      return getIsOffline() ? (
        <Stopped
          name={name}
          responseData={responseData}
          isCompressed={isCompressed}
          isFilterableByRideId={isFilterableByRideId}
          vehicleId={vehicle.id}
          loadData={handleDataFromNewRide}
          isSelectable={isCompressed && !!params}
          selectedDate={isFilterableByRideId ? undefined : selectedDate}
        />
      ) : (
        <OnlineGraph
          vehicleId={vehicle.id}
          responseData={responseData}
          isCompressed={isCompressed}
          isDetail={!!params}
          name={name}
          isFilterableByRideId={isFilterableByRideId}
          handleEndRideDateAndAddress={handleEndRideDateAndAddress}
          loadRide={handleDataAfterFinishedRide}
          isSelectable={isCompressed && !!params}
          selectedDate={isFilterableByRideId ? undefined : selectedDate}
          reloadData={reloadData}
        />
      );
    }
    return null;
  };

  const renderCompressedGraphAlert = () => {
    return getIsCompressed(responseData) ? (
      <div style={{ padding: params ? '20px 0 0 0' : '' }}>
        <CmdAlert
          id="velocity-graph-alert"
          data={{
            title: f({ id: 'graph.velocity.alert.title' }),
            message: params
              ? f({ id: 'graph.velocity.alert.message' })
              : f({ id: 'graph.onlinepanel.alert.message' }),
            type: 'info',
          }}
          noCancel
        />
      </div>
    ) : null;
  };

  const getSectionTitle = () => {
    if (name === FUEL_LEVEL_LITERS) {
      return f({ id: 'graph.fuelLevel.section.overview' }, { valueType: 'L' });
    }
    return f({ id: `graph.${name}.section.overview` });
  };

  const loadPartData = async (dateFrom: Date, dateTo: Date) => {
    if (vehicle) {
      setLoader(true);
      const response = !rideId
        ? await getPartData(name, String(vehicle.id), [dateFrom, dateTo])
        : await getPartDataByRideId(name, String(vehicle.id), rideId, [dateFrom, dateTo]);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
      }
      setLoader(false);
    }
  };

  const loadPartDataByDates = async (dateFrom: Date, dateTo: Date): Promise<void> => {
    if (vehicle) {
      setIsToday(false);
      setLoader(true);
      setSelectedPartDate([dateFrom, dateTo]);
      const response = await getPartDataByDates(
        name,
        String(vehicle.id),
        selectedPartDate || undefined,
        [dateFrom, dateTo]
      );
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
      }
      setLoader(false);
    }
  };

  const getDataByRideIdFromLocalforage = async (id: string): Promise<void> => {
    if (vehicle) {
      const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
      if (
        data &&
        data.data &&
        data.vehicleId === vehicle.id &&
        data.name === name &&
        data.rideId === id
      ) {
        setResponseData(data.data);
        setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
      } else {
        handleDataByRideId(id);
      }
    }
  };

  const resetPartData = () => {
    if (rideId) {
      getDataByRideIdFromLocalforage(rideId);
    } else {
      getDataFromLocalforage();
    }
  };

  return (
    <div
      className="container-fluid new-css-wrapper"
      data-cy="ordinary-online-graph"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {showSelectRidesModal && vehicle && (
        <SelectRidesModal
          vehicleId={String(vehicle.id)}
          closeCallback={handleModal}
          handleRide={handleRide}
          defaultRideId={rideId}
          defaultDates={defaultDates}
        />
      )}
      {params && vehicle ? (
        [
          <div className="row section page-head" data-cy="reports-head">
            <h4 data-cy="reports-title">
              {getTitle()} <span className="text-gray-light">{getVehicleName(vehicle)}</span>
            </h4>
          </div>,
          <CmdBox id="ordinary-graph-basic-information" title={getSectionTitle()}>
            {isFilterableByRideId ? (
              <div>
                <RideStartAndEnd
                  endDateAndAddress={endRideDateAndAddress}
                  rideId={rideId}
                  origStatus={origStatus}
                  handleModal={handleModal}
                  startDateAndAddress={startDateAndAddress}
                  lastDateAndAddress={lastDateAndAddress}
                />

                {isOrigCompressed && (origStatus === 'STOP' || rideId) && (
                  <PartSelecting
                    dateFrom={getDateFrom(responseData)}
                    dateTo={getDateTo(responseData)}
                    loadPartData={loadPartData}
                    loadData={resetPartData}
                    data={responseData}
                    rideId={rideId}
                  />
                )}
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '10px' }}>
                  <div
                    data-cy="datapicker-popup-fuellevelperc-graph"
                    className="label label-primary mb-xs position-relative label--filter"
                    style={{ padding: '9px 8px 8px 8px' }}
                  >
                    <span>
                      <TableDatepicker
                        name="graph-velocity"
                        type="chip"
                        onChangeCallback={handleDataByDates}
                        // defaultDates={props8.defaultDates}
                        // onlyOneDay
                        defaultDates={
                          isFilterableByRideId ? undefined : selectedPartDate || undefined
                        }
                      />
                    </span>
                  </div>
                  {rangeError && (
                    <div>
                      <CmdError message={rangeError} />
                    </div>
                  )}
                </div>
                {getIsCompressed(responseData) && (
                  <PartSelecting
                    dateFrom={getDateFrom(responseData)}
                    dateTo={getDateTo(responseData)}
                    loadPartData={loadPartDataByDates}
                    loadData={resetPartData}
                    data={responseData}
                    isNotFilterableByRide
                  />
                )}
              </div>
            )}
            {renderCompressedGraphAlert()}
            <div style={{ marginTop: '10px', position: 'relative', padding: '20px 0px 20px 25px' }}>
              {renderGraph()}
            </div>
          </CmdBox>,
          <PositionDetails vehicleId={vehicle.id} positions={responseData} />,
        ]
      ) : (
        <div>
          {renderCompressedGraphAlert()}
          {renderGraph()}
        </div>
      )}
    </div>
  );
}
